import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import DisplayConsent from "../../components/organisms/Home/InscriptionJourney/DisplayConsent";
import DisplayHasAccount from "../../components/organisms/Home/PublicJourney/DisplayHasAccount";
import DisplayStudyDetails from "../../components/organisms/Home/PublicJourney/DisplayStudyDetails";
import DisplayInclusion from "../../components/organisms/Home/InscriptionJourney/DisplayInclusion";
import DisplayLogin from "../../components/organisms/Home/LoginJourney/DisplayLogin";
import DisplayProfileSelector from "../../components/organisms/Home/PublicJourney/DisplayProfileSelector";
import { AttributeEnum, isNoSignInMethod, ProfileType } from "../../types/profile";
import {
  postConsentRequestType,
  PostUserType,
  UserStatus,
  WelcomeContentEnum,
  WelcomeType,
  WelcompeStepEnum,
} from "../../types/welcome";
import { resolveOnboarding } from "../../helper/onboarding";
import OnBoarding from "../../components/organisms/Home/InscriptionJourney/OnBoarding/OnBoarding";
import DisplayInscription from "../../components/organisms/Home/InscriptionJourney/DisplayInscription";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import StratificationPage from "../../components/organisms/Home/InscriptionJourney/StratificationPage";
import DisplayNir, { PendingNirType } from "../../components/organisms/Home/InscriptionJourney/DisplayNir";
import DisplayFamilyComposition from "../../components/organisms/Home/InscriptionJourney/DisplayFamilyComposition";
import {
  getAbsenceDeclarableRoles,
  getAutoCreateTargetRoles,
} from "../../helper/familyComposition";
import DisplaySites from "../../components/organisms/Home/InscriptionJourney/DisplaySites";
import DisplayNotIncluded from "../../components/organisms/Home/InscriptionJourney/DisplayNotIncluded";
import { useTranslation } from "react-i18next";
import { deleteNir, postConsent, postConsentByStratificationQuestions, postNewUser, postNir } from "../../services/user.service";
import { useAuth } from "../../contexts/AuthContext";
import DisplayRegistredSuccessfully from "../../components/organisms/Home/InscriptionJourney/DisplayRegistredSuccessfully";
import { useConfig } from "../../contexts/ConfigContext";
import { getCachedWelcome, getWelcomeInfo } from "../../services/weclome.service";
import useQuery from "../../hooks/useQuery";
import Info from "../../components/atoms/ErrorComponent";
import useToast from "../../hooks/useToast";
import { useFetch } from "../../hooks/useFetch";
import { getStudySlug } from "../../helper/study";
import DisplayInvitation from "../../components/organisms/Home/InscriptionJourney/DisplayInvitation";
import DisplayInitIdentification from "../../components/organisms/Home/InscriptionJourney/DisplayInitIdentification";
import { RoleEnum } from "../../types/invitation.types";
import ResetPwdModal from "../Profile/ResetPasswordModal";
import { getStudyLangs } from "../../services/study.service";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { ResonanceLayoutProvider } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useResonanceData } from "../../components/organisms/ResonanceContext/ResonanceDataContext";
import { getProjectConfig } from "../../projects";
import InscriptionProgressBar from "../../components/organisms/Home/InscriptionJourney/InscriptionProgressBar";
import { getInscriptionSteps } from "../../helper/inscriptionSteps";
import { getNirCollectionMode, shouldAskNirHolderBirthdate, shouldCollectNir } from "../../helper/nir";
import styled from "styled-components";

const DEFAULT_DISPLAY: WelcompeStepEnum = WelcompeStepEnum.HOME;

interface HomeContextType {
  welcomeData: WelcomeType;
  selectedRole: ProfileType;
  currentStep: WelcompeStepEnum;
  previousStep: WelcompeStepEnum;
  setCurrentStep(step: WelcompeStepEnum): void;
  goPrevious(): void;
  createUser(role: ProfileType): void;
  // Self-registerable authenticative subjects (e.g. father/mother) shown in the signup picker.
  signupSubjects: ProfileType[];
  // A specific role was pinned by a URL slug or invitation → skip the signup picker.
  isRoleLockedByEntry: boolean;
  // Called by the SITES step once the site is persisted; lets the journey advance from
  // SITES → CONSENT (the site is collected BEFORE consent so consent-time sequence
  // generation is already site-scoped — see the consent-resolution effect below).
  completeSiteStep(): void;
  // « Try the app » (not eligible): the account just became a test account — continue
  // to INSCRIPTION, and never show it the NIR step.
  startTryTheApp(): void;
}

const NO_PROIVDER_MESSAGE = "HomeContext - no provider";

const HomeContextDefaultValue: HomeContextType = {
  welcomeData: undefined,
  selectedRole: undefined,
  currentStep: undefined,
  previousStep: undefined,
  setCurrentStep: () => console.log(NO_PROIVDER_MESSAGE),
  goPrevious: () => console.log(NO_PROIVDER_MESSAGE),
  createUser: () => console.log(NO_PROIVDER_MESSAGE),
  signupSubjects: [],
  isRoleLockedByEntry: false,
  completeSiteStep: () => console.log(NO_PROIVDER_MESSAGE),
  startTryTheApp: () => console.log(NO_PROIVDER_MESSAGE),
};

const HomeContext = createContext<HomeContextType>(HomeContextDefaultValue);

const HomeContextProvider = HomeContext.Provider;

export const useHomeContext = () => useContext(HomeContext);

const HomeContent = ({ entrySegment }: { entrySegment?: string } = {}): ReactElement => {

  const query = useQuery();
  const invitation_token = query.get("invitation");
  const forget_password_token = query.get("forget_password_token");
  const login = query.get("login");
  const channel = query.get("channel");

  const [steps, setSteps] = useState<WelcompeStepEnum[]>([DEFAULT_DISPLAY]);
  const [isResetPwdModalOpen, setIsResetPwdModalOpen] = useState(!!forget_password_token);
  const [loading, setLoading] = useState<boolean>(false);
  const setCurrentStep = useCallback(
    (step: WelcompeStepEnum) => {
      setSteps([...steps, step]);
    },
    [steps]
  );

  const currentStep = useMemo(() => steps[steps.length - 1], [steps]);
  const previousStep = useMemo(() => steps.length > 1 && steps[steps.length - 2], [steps]);

  const goPrevious = useCallback(
    () => steps.length > 1 && setSteps(steps.slice(0, -1)),
    [steps]
  );

  const [inclusionContentId, setInclusionContentId] = useState<number | null>(
    null
  );

  const [userInfo, setUserInfo] = useState<any>();
  const [stratification, setStratification] = useState<
    postConsentRequestType["stratification_questions"] | null
  >(null);
  // Birthdate collected on the stratification step (age-based profiling); merged into the user block.
  const [profileBirthdate, setProfileBirthdate] = useState<string | null>(null);
  // NIR entered on the dedicated NIR step — held in memory like every other
  // identity field and persisted ONLY at consent time (onStratification), via
  // the dedicated cloisonné endpoint. NEVER merged into the consent payload
  // (postConsentRequestType writes the users row — cloisonnement forbids it).
  // Carries the holder's own date de naissance too when the role links the holder's
  // own care (same cloisonné endpoint, same consent-time POST).
  const [pendingNir, setPendingNir] = useState<PendingNirType | null>(null);
  // Family composition declared on the dedicated step (how many members of each
  // auto-creatable data-only role) — rides the consent body; rows created at consent.
  const [pendingFamilyComposition, setPendingFamilyComposition] = useState<
    postConsentRequestType["family_composition"] | null
  >(null);
  // Absence declarations answered on the same step (« Y a-t-il un second parent ? ») —
  // ride the consent body; the dossier_role_declarations rows are written at consent.
  const [pendingRoleAbsences, setPendingRoleAbsences] = useState<
    postConsentRequestType["role_absences"] | null
  >(null);
  const [consentId, setConsentId] = useState<number>();
  const [consent, setConsent] = useState<any>();
  // True once the (optional) site step has persisted the site. The consent-resolution effect
  // routes to SITES first when the role requests a site and this is still false, so the site
  // is known before consent generates sequences.
  const [siteDone, setSiteDone] = useState<boolean>(false);
  const completeSiteStep = useCallback(() => setSiteDone(true), []);
  // « Try the app » (DisplayInclusion / DisplayNotIncluded): the account is a test
  // account, explicitly NOT a participant — it is never asked for a NIR.
  const [isTryingTheApp, setIsTryingTheApp] = useState<boolean>(false);
  const { setRole, selectedRole, study, app } = useConfig();
  const {
    fetchSequences,
    fetchScores,
    fetchSequenceAvailability,
    prefetchFullSequencesForStudySequenceIds,
  } = useResonanceData();

  const slug = useMemo(getStudySlug, []);
  //const slug = "mentalplus";

  // console.log("slug:", slug);
  const has_consent = selectedRole?.consent?.has_consent;
  const present = useToast();
  const { getCurrent, endSession, isInitialized, startSession, user, setSignupInProgress } =
    useAuth();

  const { t, i18n } = useTranslation();

  // Seed from the persisted public welcome so the landing paints instantly while the
  // request revalidates in the background. Skip the seed for invitation flows (the
  // invitation payload isn't cached).
  const cachedWelcome = useMemo(
    () => (invitation_token ? undefined : getCachedWelcome(slug)),
    [slug, invitation_token]
  );

  const {
    isError,
    isLoading,
    data: welcomeData,
  } = useFetch<WelcomeType>(
    "welcome",
    getWelcomeInfo,
    { slug, lang: i18n.language, invitation_token },
    {
      enabled: Boolean(slug && i18n.language),
      initialData: cachedWelcome,
    }
  );

  // Subjects a participant can register as themselves (e.g. father/mother). Excludes
  // non-authenticative "data-only" subjects (enfant, pre-created) and invitation-only roles.
  const signupSubjects = useMemo(
    () => (welcomeData?.roles ?? []).filter(
      (r) =>
        r.name === RoleEnum.SUBJECT &&
        r.id != null &&
        r.attributes?.[AttributeEnum.IS_AUTHENTICATIVE] !== false &&
        !r.attributes?.[AttributeEnum.CAN_SIGN_UP_ONLY_WITH_INVITATION]
    ),
    [welcomeData]
  );

  // True when a specific role is already pinned (role-slug link or invitation) → the signup
  // picker is skipped and registration goes straight to that role's flow.
  const isRoleLockedByEntry = useMemo(() => {
    const roles = welcomeData?.roles ?? [];
    const entryMatched = !!entrySegment && entrySegment !== slug
      && roles.some((r) => r.url_slug === entrySegment && r.id != null);
    const invitationPinned = !!welcomeData?.invitations?.to_user?.profile;
    return entryMatched || invitationPinned;
  }, [welcomeData, entrySegment, slug]);

  useEffect(() => {
    const userPreferredLang = localStorage.getItem("brows_lang")
    if (userPreferredLang) {
      getStudyLangs(study.id).then(({ data }) => {
        i18n.changeLanguage(userPreferredLang);
      }).catch()
    } else {
      getStudyLangs(study.id).then(({ data }) => {
        i18n.changeLanguage(data.primary_lang);
      }).catch()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Map backend randomisation error codes (returned as the ApiError message) to i18n keys.
  const RANDOMISATION_ERROR_KEYS: Record<string, string> = {
    NO_RANDOMIZATION_SLOTS_AVAILABLE: "home_randomisation_no_slots",
    NO_MATCHING_STRATIFICATION: "home_randomisation_no_matching_group",
    INVALID_CASE_CODE: "home_randomisation_invalid_case_code",
    ALLOCATION_SOURCE_NOT_SUPPORTED: "home_randomisation_unsupported",
    SITE_REQUIRED_BEFORE_CONSENT: "home_randomisation_site_required",
  };
  const presentRandomisationError = (e: any) => {
    const key = RANDOMISATION_ERROR_KEYS[e?.response?.data?.message];
    present(key ? t(key) : (e?.response?.data?.message || t("common_server_error_desc")));
  };

  // Stable error codes of the NIR endpoint (API services/subjectNir NirCaptureErrorCode),
  // kept in production where the message is only the status text.
  const NIR_ERROR_KEYS: Record<string, string> = {
    NIR_INVALID: "form_error_nir",
    NIR_BIRTHDATE_INVALID: "form_error_nir_birthdate_invalid",
    NIR_BIRTHDATE_INCONSISTENT: "form_error_nir_birthdate_mismatch",
    NIR_OPPOSED: "home_nir_error_opposed",
  };

  const onStratification = useCallback(
    async (stratification, consent) => {
      try {
        const User = user ? user : consent.data.userAuth
        const userMock = { ...consent.data };
        delete consent.data
        const baseUser = userInfo ? userInfo.user : userMock.user;
        const body: postConsentRequestType = {
          user: profileBirthdate ? { ...baseUser, birthdate: profileBirthdate } : baseUser,
          identification_questions: userInfo ? userInfo.identification_questions : userMock.identification_questions,
          consent: consent,
          stratification_questions: stratification,
          // Family composition declared on its dedicated step — the data-only member
          // rows (e.g. Enfant) are created server-side at this consent.
          ...(pendingFamilyComposition?.length
            ? { family_composition: pendingFamilyComposition }
            : {}),
          // Absence declarations from the same step (dossier_role_declarations rows,
          // written inside the consent transaction).
          ...(pendingRoleAbsences?.length
            ? { role_absences: pendingRoleAbsences }
            : {}),
        };
        setLoading(true)
        // The NIR (if entered) is persisted only NOW — the participant is
        // consenting. Posted to its dedicated cloisonné endpoint BEFORE the
        // consent so the server-side REQUIRED check (hasSubjectNir) passes —
        // and only while the role still has a NIR step; the date only while the
        // role still asks it. An OPTIONAL step left empty withdraws a NIR an
        // earlier attempt may have stored (that consent then failed): the final
        // answer is "no NIR". On failure: toast + stay on the consent step
        // (pendingNir is kept, the click can be retried). A 404 means the study
        // no longer collects a NIR for this role: nothing to send, consent goes on.
        if (shouldCollectNir(selectedRole) && !isTryingTheApp) {
          // A REQUIRED step can never be left empty: with no NIR held (e.g. the journey
          // was reset), go back to the step instead of posting a consent the server
          // would refuse.
          if (!pendingNir && getNirCollectionMode(selectedRole) === "REQUIRED") {
            setCurrentStep(WelcompeStepEnum.NIR);
            return;
          }
          try {
            if (pendingNir) {
              await postNir(welcomeData.id, {
                nir: pendingNir.nir,
                ...(pendingNir.birthdate && shouldAskNirHolderBirthdate(selectedRole)
                  ? { birthdate: pendingNir.birthdate }
                  : {}),
              });
            } else {
              await deleteNir(welcomeData.id);
            }
          } catch (nirError) {
            if (nirError?.response?.status !== 404) {
              const nirKey = NIR_ERROR_KEYS[nirError?.response?.data?.error_code];
              present(nirKey ? t(nirKey) : (nirError?.response?.data?.message || t("common_server_error_desc")));
              return;
            }
          }
        }
        // Same i18n language the consent content was rendered with — the API snapshots
        // the per-checkbox wording in this language on the acceptance rows.
        await postConsent(welcomeData.id, User.id, body, i18n.language);
        await getCurrent();

        // Sequences are materialised at consent — now with the site/arm already known, so an
        // investigator's fan-out is correctly site-scoped (no study-wide extras to prune).
        // ResonanceInit fetched once when auth flipped and won't re-run, so refresh the shared
        // data context here; otherwise the island/sequence screens stay empty until a reload.
        const sequencesList = await fetchSequences();
        await Promise.all([fetchScores(), fetchSequenceAvailability()]);
        if (sequencesList?.length) {
          // Inline island sequences come from the per-project config (fail-soft:
          // a project without islands prefetches nothing).
          const islandSequences = getProjectConfig().islandSequenceIds ?? {};
          const studySequenceIds = Array.from(
            new Set(
              Object.values(islandSequences)
                .map((ids) => ids.inline)
                .filter(Boolean)
            )
          ) as number[];
          await prefetchFullSequencesForStudySequenceIds(studySequenceIds, sequencesList);
        }

        if (
          welcomeData.invitations?.to_user.user_status ===
          UserStatus.INCLUSION_EN_ATTENTE_DE_VALIDATION
        ) {
          setCurrentStep(WelcompeStepEnum.INVITATION);
          return;
        }

        // The site (if requested) was already collected before consent, so nothing remains
        // but the success screen.
        setCurrentStep(WelcompeStepEnum.REGISTRED_SUCCESSFULLY);
      } catch (e) {
        // The team recorded a consent for this dossier member while the page was open
        // (user_consent_grants active-row unique violation → 409 CONSENT_GRANT_CONFLICT).
        // Must be tested BEFORE the generic 409 below, which means "reservation expired".
        // Toast and stay on the CONSENT step: `loading` flipping swapped the tree for the
        // loader, so DisplayConsent remounts, refetches the content and resets its choices.
        if (
          e?.response?.data?.error_code === "CONSENT_GRANT_CONFLICT" ||
          e?.response?.data?.message === "CONSENT_GRANT_CONFLICT"
        ) {
          present(t("home_consent_grant_conflict"));
          return;
        }

        if (e?.response?.status === 409) {
          setConsentId(null);
          setStratification((current) =>
            Array.isArray(current) ? [...current] : []
          );
          present(
            "Votre reservation a expire. Le consentement est en cours de rechargement."
          );
          return;
        }

        // The consent's checkbox items changed while the page was open. Toast and stay on
        // the CONSENT step: `loading` flipping swapped the whole tree for the loader, so
        // DisplayConsent remounts here — refetching the content and resetting its
        // checkbox state. REQUIRED_CONSENT_ITEM_NOT_ACCEPTED can only reach a real client
        // through the same mid-signup edit (the UI blocks unchecked required items it
        // knows about), so it gets the same "consent changed, review again" recovery.
        if (
          e?.response?.status === 400 &&
          ["CONSENT_ITEMS_MISMATCH", "REQUIRED_CONSENT_ITEM_NOT_ACCEPTED"].includes(
            e?.response?.data?.message
          )
        ) {
          present(t("home_consent_items_mismatch"));
          return;
        }

        // Allowlist belt (nearly unreachable: the inscription pre-check already surfaces
        // this as a field error — this covers a list imported mid-signup).
        if (
          e?.response?.status === 400 &&
          e?.response?.data?.message === "External id not recognized"
        ) {
          present(t("home_inscription_error_EXTERNAL_ID_UNKNOWN"));
          return;
        }

        presentRandomisationError(e);
      }
      finally{
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userInfo, selectedRole, user, welcomeData, profileBirthdate, pendingNir, pendingFamilyComposition, pendingRoleAbsences, isTryingTheApp]
  );

  const handlePreStratification = useCallback(
    (cons) => {
      switch (selectedRole.name) {
        case RoleEnum.SUBJECT:
          (selectedRole.attributes.has_stratification || selectedRole.attributes.has_profile_attributes || selectedRole.attributes.has_birthdate) ? setCurrentStep(WelcompeStepEnum.STRATIFICATION) : onStratification([], cons);
          break;
        default:
          onStratification([], cons);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRole, userInfo, welcomeData]
  );

  // The post-identity decision (formerly inlined in handleNextInscription):
  // enter STRATIFICATION, or seed an empty stratification array which fires the
  // sites→consent effect below. Also the NIR step's continuation (onCompleted).
  const proceedAfterIdentity = useCallback(() => {
    if (
      selectedRole.attributes.has_stratification ||
      selectedRole.attributes.has_profile_attributes ||
      selectedRole.attributes.has_birthdate
    ) {
      setCurrentStep(WelcompeStepEnum.STRATIFICATION);
    }
    else {
      setStratification([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole, setCurrentStep]);

  // The continuation AFTER the (optional) family-composition step: NIR, then the
  // stratification → sites → consent chain. Shared by handleNextInscription (no
  // composition to declare) and the FAMILY_COMPOSITION step's onCompleted.
  const proceedAfterFamilyComposition = useCallback(() => {
    if (shouldCollectNir(selectedRole) && !isTryingTheApp) {
      setCurrentStep(WelcompeStepEnum.NIR);
      return;
    }
    proceedAfterIdentity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole, proceedAfterIdentity, setCurrentStep, isTryingTheApp]);

  // The study's data-only roles this registrant must declare (e.g. Enfant) — same
  // predicate as the progress-bar spine (helper/familyComposition.ts).
  const familyCompositionRoles = useMemo(
    () => getAutoCreateTargetRoles(selectedRole, welcomeData?.roles),
    [selectedRole, welcomeData]
  );
  // The roles this registrant may declare absent (e.g. Père, declarable by Mère) —
  // asked on the same FAMILY_COMPOSITION step, same shared predicate.
  const absenceRoles = useMemo(
    () => getAbsenceDeclarableRoles(selectedRole, welcomeData?.roles),
    [selectedRole, welcomeData]
  );

  const handleNextInscription = useCallback(async (data) => {
    // Family-composition / absence question(s) right after the inscription step (the
    // numéro de dossier entered there keys the skip pre-check), then the NIR screen.
    if (familyCompositionRoles.length || absenceRoles.length) {
      setCurrentStep(WelcompeStepEnum.FAMILY_COMPOSITION);
      return;
    }
    proceedAfterFamilyComposition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [has_consent, selectedRole, familyCompositionRoles, absenceRoles, proceedAfterFamilyComposition]);

  // Everything a signup journey holds for ONE role and ONE users row. A new journey (a
  // role picked again, « S'inscrire » once more) starts from scratch: nothing answered
  // for another row may be posted under the new one, and a leftover stratification
  // array would re-fire the consent-resolution effect (it re-runs on every role change)
  // straight to CONSENT, skipping the new journey's steps — NIR step included.
  const resetJourneyState = (): void => {
    setPendingNir(null);
    setPendingFamilyComposition(null);
    setPendingRoleAbsences(null);
    setStratification(null);
    setProfileBirthdate(null);
    setSiteDone(false);
    setConsentId(undefined);
    setUserInfo(undefined);
    setIsTryingTheApp(false);
  };

  // setCurrentStep closes over the step stack, so it MUST be a dependency here.
  const startTryTheApp = useCallback(() => {
    setIsTryingTheApp(true);
    setCurrentStep(WelcompeStepEnum.INSCRIPTION);
  }, [setCurrentStep]);

  const createUser = async (role: ProfileType): Promise<void> => {
    try {
      const userInfos: PostUserType = {
        role_id: role?.id,
          lang: i18n.resolvedLanguage ?? i18n.language,
      };

      const invitation = query.get("invitation");

      if (invitation) {
        userInfos.invitation_token = invitation;
      }
      
      console.log("welcomeData.id : " + welcomeData.id)
      console.log("userInfos.role_id : " + userInfos.role_id)
      console.log("userInfos.lang : " + userInfos.lang)

      // A new users row and session for THIS role (see resetJourneyState). Synchronous,
      // so it batches with the caller's setRole: the consent-resolution effect never
      // sees the new role with the previous journey's stratification.
      resetJourneyState();

      // Mark the signup flow active BEFORE the session exists: the landing route must
      // never auto-redirect to the app while the journey is running. Setting it after
      // startSession left a window in which the freshly-set user could flip
      // `isAuthenticated` and redirect out of the flow — which is what happened to
      // invited people, whose row already carries the name their inviter typed.
      setSignupInProgress(true);

      const { data } = await postNewUser(welcomeData.id, userInfos);
      await startSession(data.token);

      // Skip the onboarding step entirely when this study has nothing to show
      // ('none' — no onboarding page and no code-configured video), so the user
      // never sees an empty page. Resolution mirrors OnBoarding's.
      const onboarding = resolveOnboarding({
        studyOnboarding: app?.onboarding ?? null,
        hasContent:
          welcomeData.contents[WelcomeContentEnum.ONBOARDING_STUDY]?.id != null,
        hasCustomComponent: !!getProjectConfig().onboardingComponent,
        projectStyle: getProjectConfig().onboardingStyle,
      });
      if (onboarding.willRender) {
        setCurrentStep(WelcompeStepEnum.ON_BOARDING);
      } else {
        const has_inclusion = role?.inclusion_criteria?.has_inclusion_questions;
        setCurrentStep(
          has_inclusion
            ? WelcompeStepEnum.INCLUSION
            : WelcompeStepEnum.INSCRIPTION
        );
      }
    } catch (e) {
      // The journey never started — release the flag so the landing behaves normally.
      setSignupInProgress(false);
      console.error(e);
      present(e?.response?.data?.message);
    }
  };

  const onProfileSelection = (
    newRole: ProfileType,
    shouldAdvanceToHasAccount: boolean = true
  ): void => {
    // Back on the landing with another (or the same) role: a fresh journey.
    resetJourneyState();
    setRole(newRole);
    if (isNoSignInMethod(newRole.access.signInMode)) {
      createUser(newRole);
    } else if (shouldAdvanceToHasAccount) {
      setCurrentStep(WelcompeStepEnum.HAS_ACCOUNT);
    }
  };

  useEffect(() => {
    (async () => {
      if (isInitialized && welcomeData && currentStep === WelcompeStepEnum.HOME) {
        if (user) {
          await endSession();
        }
        if (welcomeData.roles.length > 0) {
          // Pre-select the role matching the entry URL segment (e.g. /investigator, /parent).
          // The study slug and unmatched/typo segments fall back to the study's default
          // ("main") role so the bare landing ("/", "/mentalplus") opens to whichever role
          // the researcher marked as default — falling back to the subject (then roles[0])
          // for legacy data where no role carries is_default.
          const roles = welcomeData.roles;
          // The participant-facing landing never resolves to a non-authenticative "data-only"
          // subject (enfant) — those are pre-created and never sign in.
          const isAppRole = (r: ProfileType): boolean =>
            r.id != null && !(r.name === RoleEnum.SUBJECT && r.attributes?.[AttributeEnum.IS_AUTHENTICATIVE] === false);
          const matchByEntry =
            entrySegment && entrySegment !== slug
              ? roles.find((r) => r.url_slug && r.url_slug === entrySegment && isAppRole(r))
              : undefined;
          const targetRole =
            matchByEntry ??
            roles.find((r) => r.is_default && isAppRole(r)) ??
            roles.find((r) => r.name === RoleEnum.SUBJECT && isAppRole(r)) ??
            roles.find(isAppRole) ??
            roles[0];
          if (targetRole?.access) {
            onProfileSelection(targetRole, false);
          }
        }
      }
    })();
  }, [isInitialized, welcomeData, entrySegment]);

  useEffect(
    function () {
      if (!welcomeData) return;
      const profileId = welcomeData?.invitations?.to_user?.profile;
      // Prefer the invited role; otherwise re-resolve the already-selected role against the
      // fresh list. Only apply when we actually resolve a role — never clobber a valid
      // selection with `undefined` (the invited role may be absent from the welcome list),
      // which would crash later steps that read selectedRole.access.
      const invitedRole = profileId
        ? welcomeData.roles.find((role) => role.id === profileId)
        : undefined;
      const resolvedRole =
        invitedRole ??
        (selectedRole
          ? welcomeData.roles.find((role) => role.id === selectedRole.id)
          : undefined);
      if (resolvedRole) setRole(resolvedRole);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [welcomeData]
  );

  useEffect(() => {
  if (!Array.isArray(stratification)) return;
  if (!selectedRole || !study) return;

  // Collect the site BEFORE consent: consent is what materialises sequences, so the site
  // (hence an investigator's subject scope / a subject's randomisation arm) must already be
  // persisted by then. Roles that don't request a site, or one already chosen, fall through.
  if (selectedRole?.attributes?.is_site_requested && !siteDone) {
    setCurrentStep(WelcompeStepEnum.SITES);
    return;
  }

  // If the user's role doesn't have consent, skip directly
  if (!selectedRole?.consent?.has_consent) {
    const currentConsent = {
      id: selectedRole?.consent?.id,
      is_validated: true,
      validation_date: new Date().toISOString(),
      version: 1,
      signature: "signature",
      data: userInfo
    };
    onStratification(stratification, currentConsent);
    return;
  }

  (async function fetchConsentId() {
    try {
      setLoading(true);

      const response = await postConsentByStratificationQuestions({
        studyId: study.id,
        roleId: selectedRole.id, // ok to keep if your API accepts it (even if unused)
        stratification_questions: stratification,
        // Needed for deterministic age-based profiling (birthdate not yet persisted at this step).
        birthdate: profileBirthdate,
      });

      const newConsentId = response.data?.consent_id; // number | null
      console.log("Consent ID: ", newConsentId);

      // ✅ if backend couldn't find any consent
      if (!newConsentId) {
        // choose your behavior:
        // 1) fallback to role consent id
        setConsentId(selectedRole.consent?.id);

        // or 2) block with error UI / toast
        // present("No consent found for this stratification");
        // return;

      } else {
        setConsentId(newConsentId);
      }

      setCurrentStep(WelcompeStepEnum.CONSENT);
    } catch (error) {
      console.error("Error fetching dynamic consent ID:", error);
      presentRandomisationError(error);
    } finally {
      setLoading(false);
    }
  })();
}, [stratification, selectedRole, study, siteDone]);

  

  const stepContent = useMemo(
    function () {
      const componentsByStep: Record<WelcompeStepEnum, ReactElement> = {
        HOME: <DisplayHasAccount />,
        HOME_DETAILS: <DisplayStudyDetails />,
        PROFILE_SELECTOR: <DisplayProfileSelector />,
        HAS_ACCOUNT: <DisplayHasAccount />,
        ON_BOARDING: <OnBoarding />,
        LOGIN: <DisplayLogin />,
        CONSENT: (
          <DisplayConsent
            setConsent={setConsent}
            onNextClick={(cons) => onStratification(stratification, cons)}
            consentId={consentId}
          />
        ),
        INCLUSION: (
          <DisplayInclusion setInclusionContentId={setInclusionContentId} />
        ),
        INSCRIPTION: (
          <DisplayInscription
            onNextClick={(nextClickdata) => handleNextInscription(nextClickdata)}
            saveUserInfos={setUserInfo}
            userInfo={userInfo}
          />
        ),
        // Obligatory "how many <data-only role>?" and "is there a <role>?" question(s);
        // declarations held in pendingFamilyComposition / pendingRoleAbsences and sent
        // with the consent body (rows created there).
        FAMILY_COMPOSITION: (
          <DisplayFamilyComposition
            targetRoles={familyCompositionRoles}
            absenceRoles={absenceRoles}
            externalId={userInfo?.user?.external_id ?? null}
            initialDeclarations={pendingFamilyComposition ?? null}
            initialAbsences={pendingRoleAbsences ?? null}
            onCompleted={(declarations, absences) => {
              setPendingFamilyComposition(declarations);
              setPendingRoleAbsences(absences);
              proceedAfterFamilyComposition();
            }}
          />
        ),
        // The NIR is validated on the step and held in pendingNir; the POST to
        // the dedicated cloisonné endpoint happens at consent (onStratification).
        NIR: (
          <DisplayNir
            initialNir={pendingNir}
            onCompleted={(pending) => {
              setPendingNir(pending);
              proceedAfterIdentity();
            }}
          />
        ),
        STRATIFICATION: (
          <StratificationPage
            onNextClick={async (data) => {
              setStratification(data.stratification);
              setProfileBirthdate(data.birthdate ?? null);
            }}
          />
        ),
        SITES: <DisplaySites />,
        NOT_INCLUDED: (
          <DisplayNotIncluded 
            contentId={inclusionContentId}
            onTryTheApp={startTryTheApp} 
          />
        ),
        REGISTRED_SUCCESSFULLY: <DisplayRegistredSuccessfully />,
        INVITATION: <DisplayInvitation />,
        INIT_IDENTIFICATION: <DisplayInitIdentification />,
      };

      return componentsByStep[currentStep];
    },
    // consentId MUST be a dep: after the 409 reservation-expiry recovery resets it,
    // a memo holding the stale value would remount DisplayConsent on the OLD consent.
    // stratification is captured by the CONSENT onNextClick closure above.
    [absenceRoles, consent, consentId, currentStep, familyCompositionRoles, handleNextInscription, handlePreStratification, inclusionContentId, onStratification, pendingFamilyComposition, pendingNir, pendingRoleAbsences, proceedAfterFamilyComposition, proceedAfterIdentity, startTryTheApp, stratification, userInfo]
  );

  // The registration "spine" this role walks through — one segment per step in the
  // progress bar below the header. Recomputed only when the selected role changes.
  const inscriptionSteps = useMemo(
    () => getInscriptionSteps(selectedRole, welcomeData?.roles, { skipNir: isTryingTheApp }),
    [selectedRole, welcomeData, isTryingTheApp]
  );
  const inscriptionStepIndex = inscriptionSteps.indexOf(currentStep);
  // Show the bar (and the sticky-footer shell) only on the multi-step spine.
  const showInscriptionProgress =
    inscriptionSteps.length >= 2 && inscriptionStepIndex >= 0;

  if (isLoading) return <ResonanceLoader />;
  if (loading) return <ResonanceLoader />;

  if (isError)
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
      />
    );

  return (
    <ResonanceLayoutProvider initialLayoutConfig={{ welcomePage: true, title: "" }}>
    <ResonanceLayout>
    <HomeContextProvider
      value={{
        welcomeData,
        selectedRole,
        previousStep,
        currentStep,
        goPrevious,
        setCurrentStep,
        createUser,
        signupSubjects,
        isRoleLockedByEntry,
        completeSiteStep,
        startTryTheApp,
      }}
    >
      {showInscriptionProgress ? (
        <InscriptionShell>
          <InscriptionProgressBar
            total={inscriptionSteps.length}
            current={inscriptionStepIndex}
          />
          <InscriptionStepArea>{stepContent}</InscriptionStepArea>
        </InscriptionShell>
      ) : (
        stepContent
      )}
      <ResetPwdModal
        isOpen={isResetPwdModalOpen}
        onDismiss={() => {
          setIsResetPwdModalOpen(false);
          window.history.replaceState({}, document.title, window.location.pathname);
        }}
        login={login}
        channel= {channel}
        reset_password_token={forget_password_token}
      />
    </HomeContextProvider>
    </ResonanceLayout>
  </ResonanceLayoutProvider>

  );
};

/**
 * Sticky-footer shell for the multi-step signup screens: a full-height flex column
 * so the pinned progress bar takes its natural height and the step area fills the
 * REST of the viewport. Without this, the step's own <Container> (min-height:100%)
 * would re-claim the whole viewport beneath the bar and push its NextPrev buttons
 * below the fold. min-height (not height) lets the column grow and scroll when a
 * step's content is genuinely taller than the available space.
 */
const InscriptionShell = styled.div`
  min-height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  /* Desktop reading column. The signup steps carry no max-width of their own, so
     they inherit IonContent's 1200px cap — on a laptop that is ~85% of the screen,
     and a one-question step stretched that wide reads as a broken page rather than
     a form. Constrained on the SHELL, not the step area, so the progress bar and
     the step share one column instead of a full-bleed bar over a narrow form.
     640px keeps consent prose at a comfortable measure without cramping the
     wider steps (family composition, sites). */
  @media (min-width: 768px) {
    max-width: 640px;
    margin: 0 auto;
  }
`;

const InscriptionStepArea = styled.div`
  /* Fill the space left under the bar; never shrink below the step's content so the
     footer buttons stay put and only scroll once content overflows. */
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;

  /* The step renders a single <Container> (min-height:100%). That percentage can't
     resolve here (the shell only has min-height, not a definite height), so make the
     Container fill this area by FLEX-GROW instead — which works off used sizes. Now
     short content stretches the Container to the full viewport height and its
     margin-top:auto buttons hug the bottom of the PAGE, while tall content grows the
     column and scrolls (buttons hug the bottom of the content). */
  & > * {
    flex: 1 0 auto;
  }
`;

export default HomeContent;
